import React, { ReactNode, useMemo } from 'react';

import { NavigationMap } from '@lumapps/navigation/types';

import {
    NavigationAdminVisibleByReferenceContext,
    NavigationAdminVisibleByReferenceDispatchContext,
} from './NavigationAdminVisibleByReferenceContext';
import type { NavigationAdminVisibleByReferenceContextType } from './NavigationAdminVisibleByReferenceContext';

export interface NavigationAdminVisibleByReferenceProviderProps {
    visibleByReference?: NavigationAdminVisibleByReferenceContextType;
    children: ReactNode;
}

/**
 * Provider to manage the visibleByReference object of a navigation admin.
 * This object has all the groups uses as visibility filter in the main navigation nodes.
 * A dispatch is also available to update this object if needed.
 */
export const NavigationAdminVisibleByReferenceProvider: React.FC<NavigationAdminVisibleByReferenceProviderProps> = ({
    visibleByReference = {},
    children,
}) => {
    /** State to track the newly added visibility group to the dictionary */
    const [newVisibleByGroupReferences, setNewVisibleByGroupReferences] =
        React.useState<NavigationAdminVisibleByReferenceContextType>({});

    const addVisibleByGroupReferences = React.useCallback(
        (visibleByGroupReferencesToAdd: NavigationMap['visibleByGroupReferences']) => {
            setNewVisibleByGroupReferences((oldVisibleByGroupReferences) => ({
                ...(oldVisibleByGroupReferences || {}),
                ...visibleByGroupReferencesToAdd,
            }));
        },
        [],
    );

    const value = useMemo(
        () => ({ ...visibleByReference, ...newVisibleByGroupReferences }),
        [newVisibleByGroupReferences, visibleByReference],
    );

    return (
        <NavigationAdminVisibleByReferenceContext.Provider value={value}>
            <NavigationAdminVisibleByReferenceDispatchContext.Provider value={addVisibleByGroupReferences}>
                {children}
            </NavigationAdminVisibleByReferenceDispatchContext.Provider>
        </NavigationAdminVisibleByReferenceContext.Provider>
    );
};
